import { useContext, useMemo } from 'react'
import {
  AgxBodyText,
  AgxButton,
  AgxColumn,
  AgxHeader,
  AgxRow,
  cleanFullAddress,
  formatCurrency,
  Images,
  AgxBadge,
  AgxBadgeType,
  NswContractStage,
  QldContractStage,
  PropertyAddress,
} from '@urbanx/agx-ui-components'
import { useNavigate, useParams } from 'react-router-dom'
import { MenuTitles, Pages } from 'constants/menu'
import { CampaignTabHeader } from 'components'
import { ContentLayout, PageLayout } from 'layout'
import { useContractsList } from 'hooks'
import './Contracts.scss'
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client'
import {
  CampaignDetailModelState,
  ContractAdminModel,
  ContractActionRequestContractAction,
  usePerformContractAction,
  useStaffGetCampaign,
} from '@urbanx/agx.campaigns.client'
import { useGetAgencyDetails } from '@urbanx/agx.agencies.client'
import ContractOptions from './components/ContractOptions'

const ValidQldStages = [
  QldContractStage.Accepted,
  QldContractStage.Unconditional,
  QldContractStage.Terminated,
  QldContractStage.Settled,
  QldContractStage.BackUp,
  QldContractStage.PendingCompliance,
]

const ValidNswStages = [
  NswContractStage.Signing,
  NswContractStage.BuyerSigning,
  NswContractStage.VendorSigning,
  NswContractStage.AwaitingExchange,
  NswContractStage.ContractRequested,
  NswContractStage.Accepted,
  NswContractStage.Unconditional,
  NswContractStage.Terminated,
  NswContractStage.Settled,
  NswContractStage.Cancelled,
  NswContractStage.PendingCompliance,
]

const GetStatusBadgeType = (stage?: QldContractStage | NswContractStage) => {
  switch (stage) {
    case QldContractStage.Accepted:
    case NswContractStage.Accepted:
      return AgxBadgeType.Success
    case QldContractStage.Unconditional:
    case NswContractStage.Unconditional:
      return AgxBadgeType.Waiting
    case QldContractStage.Terminated:
    case NswContractStage.Terminated:
      return AgxBadgeType.Action
    case NswContractStage.Cancelled:
      return AgxBadgeType.Action
    case QldContractStage.Settled:
    case NswContractStage.Settled:
      return AgxBadgeType.Needed
    case NswContractStage.AwaitingExchange:
    case NswContractStage.Signing:
    case NswContractStage.BuyerSigning:
    case NswContractStage.VendorSigning:
      return AgxBadgeType.Action
    case NswContractStage.ContractRequested:
      return AgxBadgeType.OnGoing
    default:
      return AgxBadgeType.Primary
  }
}

const Contracts = () => {
  const { userAccount } = useContext(AuthContext)
  const navigate = useNavigate()
  const { agencyId, campaignId } = useParams()
  const { data: agency } = useGetAgencyDetails(agencyId || '', !!agencyId)
  const { data: campaign } = useStaffGetCampaign(
    campaignId || '',
    !!campaignId
  )
  const { contracts, isLoading, invalidateContracts } = useContractsList()
  const { performContractAction } = usePerformContractAction({
    onSuccess: async () => {
      await invalidateContracts()
    },
    onError: () => {},
  })

  const filteredContracts = useMemo(() => {
    if (!contracts) return []

    if (campaign?.state === CampaignDetailModelState.NSW)
      return contracts.filter((c: ContractAdminModel) =>
        ValidNswStages.includes(c.stage as NswContractStage)
      )
    else if (campaign?.state === CampaignDetailModelState.QLD)
      return contracts.filter((c: ContractAdminModel) =>
        ValidQldStages.includes(c.stage as QldContractStage)
      )
    else return []
  }, [campaign?.state, contracts])

  const onPerformContractAction = (
    contractId: string,
    contractStageAction: ContractActionRequestContractAction
  ) => {
    if (!campaignId) return

    performContractAction({
      campaignId,
      contractId,
      contractAction: contractStageAction,
    })
  }

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as unknown as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CONTRACTS}
        isCampaigns={true}
      >
        <AgxColumn extraClasses="campaignContracts" veryLargeGap fill>
          <CampaignTabHeader
            title="All Contracts"
            titleId="allContractsTitle"
            actions={<AgxButton hollow medium disabled text="New Contract" />}
          />
          <table className="campaignContractTable">
            <tbody>
              <tr>
                <th align="left" className="column-name">
                  <AgxBodyText small>Contract</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Sale Price</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Initial Deposit</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Balance Deposit</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Status</AgxBodyText>
                </th>
                <th align="right" className="column-name action-column"></th>
              </tr>
              {!!filteredContracts &&
                filteredContracts.length > 0 &&
                filteredContracts.map((contract) => (
                  <tr
                    key={contract.contractId ?? contract.contractNames}
                    onClick={() => {
                      if (!contract.contractId) return

                      navigate(
                        `${contract.contractId}/${
                          userAccount.roles?.includes(
                            StaffRole.WorkflowContributor
                          )
                            ? Pages.CONTRACT_TASKS
                            : Pages.BUYERS_INFORMATION
                        }`
                      )
                    }}
                  >
                    <td>
                      <AgxRow mediumGap centered>
                        <Images.SparkleSmall />
                        <AgxHeader size={4}>{contract.contractNames}</AgxHeader>
                      </AgxRow>
                    </td>
                    <td>
                      <AgxColumn fill smallGap>
                        {contract.salePrice
                          ? formatCurrency(contract.salePrice, 0)
                          : '$--'}
                      </AgxColumn>
                    </td>
                    <td>
                      <AgxColumn fill smallGap>
                        {contract.initialDeposit
                          ? formatCurrency(contract.initialDeposit)
                          : '$--'}
                      </AgxColumn>
                    </td>
                    <td>
                      <AgxColumn fill smallGap>
                        {contract.balanceDeposit
                          ? formatCurrency(contract.balanceDeposit)
                          : '$--'}
                      </AgxColumn>
                    </td>
                    <td>
                      <AgxBadge
                        text={contract.stage ?? ''}
                        type={GetStatusBadgeType(
                          contract.stage as QldContractStage | NswContractStage
                        )}
                      />
                    </td>
                    <td className="contractOptionsCell">
                      {contract.contractId && (
                        <ContractOptions
                          contractId={contract.contractId}
                          campaignState={campaign?.state}
                          stage={
                            contract.stage as QldContractStage | NswContractStage
                          }
                          onPerformContractAction={onPerformContractAction}
                          dataTestId={`contract-options-${contract.contractId}`}
                        />
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {(!filteredContracts || filteredContracts.length === 0) && (
            <AgxColumn
              fill
              centered
              centerJustified
              extraClasses="contractDetailNoContracts"
            >
              <AgxBodyText>
                {isLoading
                  ? 'Loading...'
                  : 'No Contracts have been submitted for this campaign'}
              </AgxBodyText>
            </AgxColumn>
          )}
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  )
}

export default Contracts
